import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Collapse } from "react-bootstrap";
import Select from "react-select";
import AsyncSearchSelect from "../common/AsyncSearchSelect";
import { fetchAjaxListData } from "../../utils";
import { useParams, useSearchParams } from "react-router-dom";
import { MdKeyboardArrowDown, MdContentCopy, MdDeleteOutline, MdEdit, MdOutlineCheckCircle, MdOutlineRemoveCircleOutline, MdAddCircle, MdFileDownload } from "react-icons/md";
import { FiRefreshCw } from "react-icons/fi";
import { BsThreeDotsVertical } from "react-icons/bs";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { Utils, fetchWithAuth, selectStyles, copyToClipboard, getStatusDisplay, handleSessionExpired, getDefaultPageSize, DEFAULT_STATUS_OPTIONS, can, getAvatarInitials, nsAvatarColor, exportListCsv } from "../../utils";
import { useActionDropdown } from "../../hooks/useActionDropdown";
import { useTableMaxHeight } from "../../hooks/useTableMaxHeight";
import { useScrollEndDetection } from "../../hooks/useScrollEndDetection";
import ListPaginationBar from "../common/ListPaginationBar";

const AppSupportGroups = () => {
  const { appId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const pageSize = getDefaultPageSize();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ page: 1, totalPages: 1, total: 0 });
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ group_id: "", name: "", status: "" });
  const [draftFilters, setDraftFilters] = useState({ group_id: "", name: "", status: "" });
  const [filterAccordionOpen, setFilterAccordionOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const tableScrollRef = useRef(null);
  const tableInnerRef = useRef(null);
  const [form, setForm] = useState({ id: null, group_id: "", name: "", status: 1, team_member_ids: [] });
  const tableMaxHeight = useTableMaxHeight(tableScrollRef);
  const isAtScrollEnd = useScrollEndDetection(tableScrollRef, tableInnerRef, rows.length);
  const getGroupActionOptionCount = () =>
    1 +
    (can("support.groups.edit") ? 1 : 0) +
    (can("support.groups.change_status") ? 1 : 0) +
    (can("support.groups.delete") ? 1 : 0);
  const { openActionId, openActionItem, dropdownPosition, dropdownRef, handleDropdownAction, handleOpenDropdown } = useActionDropdown({
    tableScrollRef,
    optionCount: getGroupActionOptionCount,
    menuWidth: 220,
    useAnchored: true,
  });

  const loadData = async (nextPage = page, nextFilters = filters) => {
    setLoading(true);
    const res = await fetchWithAuth(`${Utils.baseurl}application/support/groups`, {
      method: "POST",
      body: JSON.stringify({
        app_id: appId,
        page: nextPage,
        pageSize,
        group_id: (nextFilters.group_id || "").trim(),
        name: (nextFilters.name || "").trim(),
        status: nextFilters.status,
      }),
    });
    const result = await res.json().catch(() => ({}));
    setLoading(false);
    if (handleSessionExpired(res, result)) return;
    if (!result.status) return toast.error(result.message || "Failed to load groups");
    setRows(result.data || []);
    setMeta(result.meta || { page: 1, totalPages: 1, total: 0 });
  };

  const fetchTeamMemberOpts = useCallback(
    ({ search, ids }) =>
      fetchAjaxListData({
        type: "support-team-members",
        q: search,
        id: ids,
        extra: { app_id: appId },
        mode: "options",
      }),
    [appId]
  );

  useEffect(() => {
    if (!appId) return;
    const p = Math.max(1, parseInt(searchParams.get("page") || "1", 10));
    const group_id = searchParams.get("group_id") || "";
    const name = searchParams.get("name") || "";
    const status = searchParams.get("status") || "";
    const f = { group_id, name, status };
    setPage(p);
    setFilters(f);
    setDraftFilters(f);
    setFilterAccordionOpen(Boolean(group_id || name || status));
    loadData(p, f);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId, searchParams]);

  const writeParams = (p, f) => {
    const sp = new URLSearchParams();
    if (p > 1) sp.set("page", String(p));
    if ((f.group_id || "").trim()) sp.set("group_id", f.group_id.trim());
    if ((f.name || "").trim()) sp.set("name", f.name.trim());
    if (String(f.status || "").trim()) sp.set("status", String(f.status).trim());
    setSearchParams(sp, { replace: true });
  };

  const onApplyFilters = () => {
    const f = { ...draftFilters };
    setFilters(f);
    setPage(1);
    setFilterAccordionOpen(Boolean((f.group_id || "").trim() || (f.name || "").trim() || (f.status || "").trim()));
    writeParams(1, f);
  };

  const onResetFilters = () => {
    const f = { group_id: "", name: "", status: "" };
    setDraftFilters(f);
    setFilters(f);
    setPage(1);
    setFilterAccordionOpen(false);
    writeParams(1, f);
  };


  const save = async () => {
    if (!form.name) return toast.error("Name is required");
    const res = await fetchWithAuth(`${Utils.baseurl}application/support/group/save`, {
      method: "POST",
      body: JSON.stringify({ app_id: appId, ...form, team_member_ids: Array.isArray(form.team_member_ids) ? form.team_member_ids : [] }),
    });
    const result = await res.json().catch(() => ({}));
    if (!result.status) return toast.error(result.message || "Failed to save group");
    toast.success("Group saved");
    setShowModal(false);
    setForm({ id: null, group_id: "", name: "", status: 1, team_member_ids: [] });
    loadData();
  };

  const toggleGroupStatus = async (item) => {
    if (!item?.id) return;
    const nextStatus = Number(item.status) === 1 ? 0 : 1;
    const res = await fetchWithAuth(`${Utils.baseurl}application/support/group/save`, {
      method: "POST",
      body: JSON.stringify({
        app_id: appId,
        id: item.id,
        group_id: item.group_id,
        name: item.name,
        status: nextStatus,
        team_member_ids: Array.isArray(item.team_member_ids) ? item.team_member_ids : [],
      }),
    });
    const result = await res.json().catch(() => ({}));
    if (!result.status) return toast.error(result.message || "Failed to update group status");
    toast.success(nextStatus === 1 ? "Group activated" : "Group deactivated");
    loadData();
  };

  const remove = async (id) => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "This group will be permanently deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete",
    });
    if (!confirm.isConfirmed) return;
    const res = await fetchWithAuth(`${Utils.baseurl}application/support/group/delete`, {
      method: "POST",
      body: JSON.stringify({ app_id: appId, id }),
    });
    const result = await res.json().catch(() => ({}));
    if (!result.status) return toast.error(result.message || "Delete failed");
    toast.success("Group deleted successfully");
    loadData();
  };


  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const { status, message } = await exportListCsv("application/support/groups", {
        app_id: appId,
        group_id: (filters.group_id || "").trim(),
        name: (filters.name || "").trim(),
        status: filters.status,
      }, "support-groups.csv");
      if (!status) {
        toast.error(message);
        return;
      }
      if (message) toast.info(message);
    } catch (error) {
      console.error(error);
      toast.error("Export failed");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="dashboard_right">
      <div className="dashboard_inner ns-support-list">
        <div className="dashboard_right_top">
          <div className="dashboard_heading">
            <h1>Support Groups</h1>
          </div>
          <div className="add_app" style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <button type="button" className="add_app_btn_secondary btn_theme_secondary" title="Refresh list" onClick={() => loadData()}>
              <FiRefreshCw />
              <span>Refresh</span>
            </button>
            <button type="button" className="add_app_btn_secondary btn_theme_secondary" title="Export CSV for applied filters" onClick={handleExport} disabled={isExporting}>
              <MdFileDownload />
              <span>{isExporting ? "Exporting…" : "Export"}</span>
            </button>
            {can("support.groups.add") ? (
              <button
                type="button"
                className="add_app_btn btn_theme_primary"
                onClick={() => {
                  setForm({ id: null, group_id: "", name: "", status: 1, team_member_ids: [] });
                  setShowModal(true);
                }}
              >
                <MdAddCircle />
                <span>Add Group</span>
              </button>
            ) : null}
          </div>
        </div>

        <div className="list-page-filter-card">
          <button type="button" className="list-page-filter-accordion-header" onClick={() => setFilterAccordionOpen((o) => !o)} aria-expanded={filterAccordionOpen}>
            <span className="list-page-filter-accordion-title">Filter</span>
            <MdKeyboardArrowDown className={`list-page-filter-accordion-chevron ${filterAccordionOpen ? "list-page-filter-accordion-chevron--open" : ""}`} size={22} />
          </button>
          <Collapse in={filterAccordionOpen}>
            <div className="list-page-filter-accordion-body">
              <div className="list-page-filter-bar">
                <div className="list-page-filter-fields row g-3 align-items-end">
                  <div className="list-page-filter-field col-12 col-lg-4">
                    <input className="form-control" placeholder="Group ID" value={draftFilters.group_id} onChange={(e) => setDraftFilters((p) => ({ ...p, group_id: e.target.value }))} />
                  </div>
                  <div className="list-page-filter-field col-12 col-lg-4">
                    <input className="form-control" placeholder="Name" value={draftFilters.name} onChange={(e) => setDraftFilters((p) => ({ ...p, name: e.target.value }))} />
                  </div>
                  <div className="list-page-filter-field col-12 col-lg-4">
                    <Select
                      inputId="support-groups-filter-status"
                      instanceId="support-groups-filter-status"
                      classNamePrefix="ns-support-groups-filter-status"
                      isSearchable
                      isClearable
                      placeholder="All Status"
                      options={DEFAULT_STATUS_OPTIONS}
                      value={DEFAULT_STATUS_OPTIONS.find((o) => String(o.value) === String(draftFilters.status)) || null}
                      onChange={(opt) => setDraftFilters((p) => ({ ...p, status: opt?.value ?? "" }))}
                      styles={selectStyles}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                    />
                  </div>
                  <div className="list-page-filter-field list-page-filter-actions col-12 col-lg-12">
                    <div className="add_app">
                      <button type="button" className="add_app_btn btn_theme_primary" onClick={onApplyFilters}><span>Search</span></button>
                      <button type="button" className="add_app_btn_secondary btn_theme_secondary" onClick={onResetFilters}><span>Reset</span></button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Collapse>
        </div>

        <div className="dashboard_app_table dashboard_app_table--list-records">
          <div className="dashboard_table">
            <div ref={tableScrollRef} className="table_scroll_wrapper">
              <div
                ref={tableInnerRef}
                className={`table_inner table_y_scroll ${isAtScrollEnd ? "at-scroll-end" : ""}`}
                style={{
                  maxHeight: tableMaxHeight ? `${tableMaxHeight}px` : "calc(100vh - 320px)",
                  overflowY: "auto",
                  overflowX: "auto",
                }}
              >
                <table className="table_outer table-sm">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Team members</th>
                      <th>Status</th>
                      <th className="col-action" style={{ textAlign: "right" }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? <tr><td colSpan="4" className="text-center">Loading...</td></tr> : rows.length === 0 ? <tr><td colSpan="4" className="text-center">No groups found</td></tr> : rows.map((r) => (
                      <tr key={r.id}>
                        <td>
                          {(() => {
                            const c = nsAvatarColor(r.group_id || r.name);
                            return (
                              <div className="ns-requester">
                                <span className="ns-avatar" style={{ background: c.bg, color: c.text }}>
                                  {getAvatarInitials(r.name)}
                                </span>
                                <div style={{ minWidth: 0 }}>
                                  <div className="ns-requester-name">{r.name || "-"}</div>
                                </div>
                              </div>
                            );
                          })()}
                        </td>
                        <td style={{ minWidth: 180 }}>
                          {Array.isArray(r.team_members) && r.team_members.length
                            ? r.team_members.map((a) => a.name || a.team_member_id).join(", ")
                            : "-"}
                        </td>
                        <td>
                          {(() => {
                            const st = getStatusDisplay(r.status);
                            return <span className={st.badgeClass}>{st.label}</span>;
                          })()}
                        </td>
                        <td className="col-action" style={{ textAlign: "right" }}>
                          <div className="support-actions-menu" style={{ position: "relative", display: "inline-block" }}>
                            <button
                              type="button"
                              className="dropdown-trigger-btn btn btn-link p-0"
                              aria-label="Actions"
                              aria-expanded={openActionId === r.id}
                              onClick={(e) => handleOpenDropdown(e, r.id, r)}
                            >
                              <BsThreeDotsVertical />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <ListPaginationBar
                page={page}
                setPage={(n) => writeParams(n, filters)}
                total={meta.total || 0}
                pageSize={pageSize}
                totalPages={meta.totalPages || 1}
                ariaLabel="Support groups pagination"
                loading={loading}
              />
            </div>
          </div>
        </div>

        {showModal ? (
          <div className="modal d-block ns-modal" tabIndex="-1" role="dialog" style={{ background: "rgba(0,0,0,0.35)" }}>
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{form.id ? "Edit Group" : "Add Group"}</h5>
                  <button type="button" className="btn-close ms-auto" aria-label="Close" onClick={() => setShowModal(false)} />
                </div>
                <div className="modal-body">
                  <div className="mb-2"><label className="form-label">Name</label><input className="form-control" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} /></div>
                  <div className="mb-2">
                    <label className="form-label">Team members</label>
                    <AsyncSearchSelect
                      inputId="support-groups-modal-team-members"
                      instanceId="support-groups-modal-team-members"
                      classNamePrefix="ns-support-groups-modal-team-members"
                      isMulti
                      closeMenuOnSelect={false}
                      placeholder="Type to search team members…"
                      fetchOptions={fetchTeamMemberOpts}
                      value={form.team_member_ids || []}
                      onChange={(opts) =>
                        setForm((p) => ({
                          ...p,
                          team_member_ids: Array.isArray(opts) ? opts.map((x) => String(x.value)) : [],
                        }))
                      }
                      styles={selectStyles}
                    />
                  </div>
                  <div className="mb-2">
                    <label className="form-label">Status</label>
                    <Select
                      inputId="support-groups-modal-status"
                      instanceId="support-groups-modal-status"
                      classNamePrefix="ns-support-groups-modal-status"
                      isSearchable={false}
                      isClearable={false}
                      options={DEFAULT_STATUS_OPTIONS}
                      value={DEFAULT_STATUS_OPTIONS.find((o) => o.value === Number(form.status)) || DEFAULT_STATUS_OPTIONS[0]}
                      onChange={(opt) => setForm((p) => ({ ...p, status: Number(opt?.value ?? 1) }))}
                      styles={selectStyles}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  {can(form.id ? "support.groups.edit" : "support.groups.add") ? (
                    <button type="button" className="add_app_btn btn_theme_primary" onClick={save}><span>Save</span></button>
                  ) : null}
                  <button type="button" className="add_app_btn_secondary btn_theme_secondary" onClick={() => setShowModal(false)}><span>Close</span></button>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
      {openActionItem && dropdownPosition
        ? createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: "fixed",
              left: dropdownPosition.left,
              top: dropdownPosition.top,
              zIndex: 999999,
              minWidth: "170px",
            }}
          >
            <div className="dropdown-menu-subscription">
              <button
                type="button"
                onClick={() => handleDropdownAction(async () => {
                  const id = openActionItem.group_id;
                  if (!id) return toast.error("Group ID not available");
                  await copyToClipboard(id);
                  toast.success("Group ID copied");
                })}
                style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", textAlign: "left" }}
              >
                <MdContentCopy size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                <span style={{ fontSize: "0.875rem" }}>Copy Group ID</span>
              </button>
              {can("support.groups.edit") ? (
              <button
                type="button"
                onClick={() => handleDropdownAction(() => {
                  setForm({
                    id: openActionItem.id,
                    group_id: openActionItem.group_id,
                    name: openActionItem.name,
                    status: Number(openActionItem.status) === 0 ? 0 : 1,
                    team_member_ids: Array.isArray(openActionItem.team_member_ids) ? openActionItem.team_member_ids : [],
                  });
                  setShowModal(true);
                })}
                style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", textAlign: "left" }}
              >
                <MdEdit size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                <span style={{ fontSize: "0.875rem" }}>Edit Group</span>
              </button>
              ) : null}
              {can("support.groups.change_status") ? (
              <button
                type="button"
                onClick={() => handleDropdownAction(() => toggleGroupStatus(openActionItem))}
                style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", textAlign: "left" }}
              >
                {Number(openActionItem.status) === 1 ? (
                  <MdOutlineRemoveCircleOutline size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                ) : (
                  <MdOutlineCheckCircle size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                )}
                <span style={{ fontSize: "0.875rem" }}>{Number(openActionItem.status) === 1 ? "Deactivate" : "Activate"}</span>
              </button>
              ) : null}
              {can("support.groups.delete") ? (
              <button
                type="button"
                className="delete-class"
                onClick={() => handleDropdownAction(() => remove(openActionItem.id))}
                style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", textAlign: "left" }}
              >
                <MdDeleteOutline size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                <span style={{ fontSize: "0.875rem" }}>Delete Group</span>
              </button>
              ) : null}
            </div>
          </div>,
          document.body
        )
        : null}
    </div>
  );
};

export default AppSupportGroups;
